import { env } from '@/config/env'

/**
 * lib/apiClient.ts
 * thin fetch wrapper with:
 *   - setTokenAcquirer(fn) — wired from main.tsx after MSAL initialises
 *   - PascalCase output (for .NET API compatibility)
 *   - camelCase input (for React component compatibility)
 *   - apiGet / apiPost / apiPut / apiDelete helpers consumed by src/services/
 *
 * No MSAL import here — the token function is injected at startup, keeping
 * this module free of auth concerns.
 */

// ── Token acquirer ─────────────────────────────────────────────────────────────
type TokenAcquirer = () => Promise<string>

let _tokenAcquirer: TokenAcquirer | null = null

/**
 * Called once from main.tsx after msalInstance.initialize() completes.
 * Injects the token-acquisition function so apiClient never imports MSAL directly.
 */
export function setTokenAcquirer(fn: TokenAcquirer): void {
  _tokenAcquirer = fn
}

// ── Key case conversion ────────────────────────────────────────────────────────
function toPascalCase(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1)
}

function toCamelCase(key: string): string {
  return key.charAt(0).toLowerCase() + key.slice(1)
}

function convertKeys<T>(obj: unknown, convert: (k: string) => string): T {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeys(item, convert)) as T
  }
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([k, v]) => [
        convert(k),
        convertKeys(v, convert),
      ]),
    ) as T
  }
  return obj as T
}

// ── Core fetch wrapper ─────────────────────────────────────────────────────────
async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const base = env.VITE_API_BASE_URL.replace(/\/$/, '')
  const url  = `${base}/${path.replace(/^\//, '')}`

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept':       'application/json',
  }

  if (env.VITE_API_KEY) {
    headers['Api-Key-Value'] = env.VITE_API_KEY
  } else if (_tokenAcquirer) {
    const token = await _tokenAcquirer().catch(() => '')
    if (token) headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined
      ? JSON.stringify(convertKeys(body, toPascalCase))
      : undefined,
  })

  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`)
  }

  // 204 No Content
  if (response.status === 204) return undefined as T

  const data = await response.json() as unknown
  return convertKeys<T>(data, toCamelCase)
}

// ── Public helpers consumed by src/services/ ───────────────────────────────────
export const apiGet    = <T>(path: string)                  => request<T>('GET',    path)
export const apiPost   = <T>(path: string, body?: unknown)  => request<T>('POST',   path, body)
export const apiPut    = <T>(path: string, body?: unknown)  => request<T>('PUT',    path, body)
export const apiDelete = <T>(path: string)                  => request<T>('DELETE', path)

/**
 * POST to an endpoint that returns a binary stream (e.g. Report/GenerateReport).
 * Mirrors the auth logic of `request()` but returns a Blob instead of parsed JSON.
 * Used by useExcelExport to trigger server-side file generation.
 */
export async function apiBlobPost(path: string, body?: unknown): Promise<Blob> {
  const base = env.VITE_API_BASE_URL.replace(/\/$/, '')
  const url  = `${base}/${path.replace(/^\//, '')}`

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept':       'application/octet-stream',
  }

  if (env.VITE_API_KEY) {
    headers['Api-Key-Value'] = env.VITE_API_KEY
  } else if (_tokenAcquirer) {
    const token = await _tokenAcquirer().catch(() => '')
    if (token) headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: body !== undefined
      ? JSON.stringify(convertKeys(body, toPascalCase))
      : undefined,
  })

  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`)
  }

  return response.blob()
}

/**
 * GET an endpoint that returns a binary stream.
 * Used by attendance bulk export downloads.
 */
export async function apiBlobGet(path: string): Promise<Blob> {
  const base = env.VITE_API_BASE_URL.replace(/\/$/, '')
  const url  = `${base}/${path.replace(/^\//, '')}`

  const headers: Record<string, string> = {
    'Accept': 'application/octet-stream',
  }

  if (env.VITE_API_KEY) {
    headers['Api-Key-Value'] = env.VITE_API_KEY
  } else if (_tokenAcquirer) {
    const token = await _tokenAcquirer().catch(() => '')
    if (token) headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, {
    method: 'GET',
    headers,
  })

  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`)
  }

  return response.blob()
}

/**
 * @deprecated Use apiGet / apiPost / apiPut / apiDelete from '@/lib/apiClient' directly.
 * Kept as a named export so legacy imports (src/services/apiClient.ts stub) still resolve.
 */
export const apiClient = { get: apiGet, post: apiPost, put: apiPut, delete: apiDelete }

